// KI-Bewertung eines Leads (ERP/CRM-Plan, Abschnitt 4 „KI-Scoring").
//
// Gemini bewertet einen Lead anhand von Branche, Region, Naehe zu aktiven
// Projekten und (optional) Websitetext und liefert `score` (0..100) plus eine
// Begruendung. Beides wird am Lead gespeichert und geht anschliessend in
// leadLogic.computeLeadPriority ein.
//
// Aufbau wie im Rest des Moduls: Prompt-Bau und Antwort-Auswertung sind PURE
// Funktionen und getestet; dazwischen liegt genau EIN unreiner Aufruf ueber
// den bestehenden gemini-proxy-Weg (geminiService.callAiBackend — dieselbe
// Auth- und Fehlerbehandlung wie alle anderen KI-Funktionen).
//
// SCORING IST OPTIONAL. Jeder Fehler wird still getragen: ein ausgefallener
// KI-Dienst darf die Pipeline nicht blockieren, und ein Lead ohne Score ist
// nicht schlechter, sondern nur unbewertet (computeLeadPriority rechnet dann
// mit einem neutralen Mittelwert).

import { callAiBackend } from './geminiService';
import { parseAiResponse } from './geminiNormalizers';
import { brancheLabel } from './branchen';

// ─── Eingabe ─────────────────────────────────────────────────────────────────

export interface LeadScoringInput {
    name: string;
    branche?: string | null;
    zip?: string | null;
    city?: string | null;
    bundesland?: string | null;
    website?: string | null;
    /** Optionaler Websitetext (Impressum/Startseite), roh. */
    websiteText?: string | null;
    /** Entfernung zum naechsten aktiven Projekt in km, null wenn unbekannt. */
    distanceKm?: number | null;
    /** Name des naechsten aktiven Projekts — macht die Begruendung konkret. */
    nearestProjectName?: string | null;
    /** Wie viele aktive Projekte es im Umkreis von 100 km gibt. */
    projekteImUmkreis?: number;
}

export interface LeadScore {
    /** 0..100, ganzzahlig. */
    score: number;
    begruendung: string;
}

/**
 * Websitetext wird gekuerzt in den Prompt gegeben. Eine vollstaendige
 * Unternehmensseite sprengt sonst das Token-Budget und kostet je Lead ein
 * Vielfaches — die ersten 2000 Zeichen tragen die Selbstbeschreibung.
 */
export const WEBSITE_TEXT_MAX = 2000;

const zeile = (label: string, value?: string | null): string | null => {
    const v = (value || '').trim();
    return v ? `- ${label}: ${v}` : null;
};

/**
 * Der Bewertungs-Prompt.
 *
 * Drei Festlegungen, die hier bewusst stehen:
 *  1. Das Zielprofil (SiGeKo nach BaustellV) steht IM Prompt. Ohne es bewertet
 *     das Modell „ist das eine gute Firma?" statt „ist das ein guter Kunde
 *     fuer uns?".
 *  2. Fehlende Angaben duerfen NICHT erfunden werden — dieselbe Regel, die im
 *     Proxy fuer alle anderen Aktionen gilt (FACTUALITY). Ein halluzinierter
 *     Grund waere schlimmer als gar keiner, weil er ueberzeugend klingt.
 *  3. Die Antwortform ist eng vorgegeben (JSON, zwei Felder), damit
 *     parseLeadScore nicht raten muss.
 */
export const buildLeadScoringPrompt = (input: LeadScoringInput): string => {
    const websiteText = (input.websiteText || '').trim().slice(0, WEBSITE_TEXT_MAX);

    const naehe = input.distanceKm == null
        ? '- Entfernung zum nächsten laufenden Projekt: unbekannt (Lead nicht verortet)'
        : `- Entfernung zum nächsten laufenden Projekt: ${Math.round(input.distanceKm)} km` +
          (input.nearestProjectName ? ` (${input.nearestProjectName})` : '');

    const angaben = [
        zeile('Firma', input.name),
        zeile('Branche', input.branche ? brancheLabel(input.branche) : ''),
        zeile('Ort', [input.zip, input.city].filter(Boolean).join(' ')),
        zeile('Bundesland', input.bundesland),
        zeile('Website', input.website),
        naehe,
        typeof input.projekteImUmkreis === 'number'
            ? `- Eigene laufende Projekte im Umkreis von 100 km: ${input.projekteImUmkreis}`
            : null,
    ].filter(Boolean).join('\n');

    return `Du bewertest einen Vertriebs-Lead für ein deutsches SiGeKo-Büro.

Das Büro koordiniert Sicherheit und Gesundheitsschutz auf Baustellen nach
Baustellenverordnung (BaustellV) und RAB 30. Gute Kunden sind Bauherren und
deren Vertreter, die regelmäßig Bauvorhaben mit mehreren Gewerken beauftragen
— also Bauträger, Generalunternehmer, Projektentwickler, Wohnungswirtschaft,
Kommunen und Industriebetreiber mit eigenem Baubestand. Architektur- und
Ingenieurbüros sind eher Mittler als Auftraggeber. Regionale Nähe zu bereits
laufenden Projekten senkt die Anfahrtskosten je Begehung erheblich und ist
deshalb ein echter wirtschaftlicher Vorteil.

Angaben zum Lead:
${angaben}
${websiteText ? `\nAuszug von der Website des Unternehmens:\n"""\n${websiteText}\n"""\n` : ''}
Bewerte, wie aussichtsreich dieser Lead als SiGeKo-Kunde ist.

KRITISCH: Nutze AUSSCHLIESSLICH die oben genannten Angaben. Erfinde keine
Umsätze, Mitarbeiterzahlen, Bauvorhaben oder Referenzen, die dort nicht stehen.
Fehlen Angaben, sage das in der Begründung und bewerte entsprechend
zurückhaltend (Richtung 50).

Antworte AUSSCHLIESSLICH mit einem validen JSON-Objekt ohne weiteren Text:
{
  "score": <ganze Zahl 0-100, 0 = chancenlos, 50 = unklar, 100 = idealer Kunde>,
  "begruendung": "<ein bis zwei Sätze auf Deutsch, die genau diese Zahl erklären>"
}`;
};

// ─── Antwort auswerten ───────────────────────────────────────────────────────

const clamp100 = (n: number): number => Math.max(0, Math.min(100, n));

/**
 * Antwort des Modells -> Score. `null`, wenn nichts Brauchbares darin steht.
 *
 * Tolerant gegen die drei Formen, die real vorkommen: JSON-Objekt, in Markdown
 * eingepackter JSON-Text (das erledigt parseAiResponse) und ein Objekt mit
 * abweichender Feldschreibweise. NICHT tolerant gegen fehlende oder
 * unplausible Zahlen — ein erratener Score waere schlimmer als keiner, weil er
 * anschliessend die Arbeitsreihenfolge bestimmt.
 */
export const parseLeadScore = (raw: unknown): LeadScore | null => {
    const parsed: any = parseAiResponse(raw);
    const obj = Array.isArray(parsed) ? parsed[0] : parsed;
    if (!obj || typeof obj !== 'object') return null;

    const rohScore = obj.score ?? obj.bewertung ?? obj.punkte ?? obj.rating;
    if (rohScore === null || rohScore === undefined || rohScore === '') return null;
    const score = Number(rohScore);
    if (!Number.isFinite(score)) return null;

    const begruendung = String(
        obj.begruendung ?? obj.begründung ?? obj.reason ?? obj.reasoning ?? obj.erklaerung ?? '',
    ).trim();

    return {
        score: Math.round(clamp100(score)),
        // Ohne Begruendung bleibt der Score trotzdem gueltig — aber die
        // Oberflaeche soll sichtbar machen, dass die Erklaerung fehlt, statt
        // ein leeres Feld zu zeigen.
        begruendung: begruendung || 'Keine Begründung geliefert.',
    };
};

// ─── Unreiner Teil: der eine Proxy-Aufruf ───────────────────────────────────

/**
 * Lead bewerten lassen. `null` bei JEDEM Fehler — kein Werfen.
 *
 * Begruendung fuer das stille Scheitern: das Scoring ist eine Zutat, keine
 * Voraussetzung. Ein Netzfehler, ein abgelaufenes Token oder ein noch nicht
 * ausgerollter Proxy (die Aktion `scoreLead` braucht ein Deploy der Edge
 * Function) darf weder das Board blockieren noch einen Fehlerdialog erzeugen,
 * den der Anwender nicht beheben kann. Die Ursache landet auf der Konsole.
 */
export const scoreLead = async (input: LeadScoringInput): Promise<LeadScore | null> => {
    try {
        const promptText = buildLeadScoringPrompt(input);
        const raw = await callAiBackend('scoreLead', { promptText });
        return parseLeadScore(raw);
    } catch (error) {
        console.warn('Lead-Scoring fehlgeschlagen (Scoring ist optional):', error);
        return null;
    }
};
